import { BaseDataRecord } from '../types/report';

function escapeCsvCell(val: any): string {
  if (val === null || val === undefined) {
    return '';
  }
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export interface AvailableCsvColumns {
  mainKeys: string[];
  detailKeys: string[];
}

export function getAvailableCsvColumns(records: BaseDataRecord[]): AvailableCsvColumns {
  if (!records || records.length === 0) {
    return { mainKeys: [], detailKeys: [] };
  }

  // Discover all Main column keys (excluding attandanceInfo & employeeUUID)
  const mainKeysSet = new Set<string>();
  mainKeysSet.add('internalId');
  mainKeysSet.add('name');
  mainKeysSet.add('joiningDate');
  mainKeysSet.add('resignationDate');

  for (const emp of records) {
    for (const k of Object.keys(emp)) {
      if (k !== 'attandanceInfo' && k !== 'attendanceInfo' && k !== 'employeeUUID') {
        mainKeysSet.add(k);
      }
    }
  }

  // Discover all Details column keys
  const detailKeysSet = new Set<string>();
  detailKeysSet.add('Date');
  detailKeysSet.add('inTime');
  detailKeysSet.add('outTime');
  detailKeysSet.add('isLate');
  detailKeysSet.add('LateMinute');
  detailKeysSet.add('isAbsence');
  detailKeysSet.add('isOnLeave');
  detailKeysSet.add('LeaveType');
  detailKeysSet.add('PunchError');
  detailKeysSet.add('isPresentInHoliday');
  detailKeysSet.add('isHoliday');
  detailKeysSet.add('isWeeklyOff');
  detailKeysSet.add('shiftName');

  for (const emp of records) {
    const dailyRecords = emp.attandanceInfo || emp.attendanceInfo;
    if (dailyRecords) {
      for (const row of dailyRecords) {
        for (const k of Object.keys(row)) {
          detailKeysSet.add(k);
        }
      }
    }
  }

  return {
    mainKeys: Array.from(mainKeysSet),
    detailKeys: Array.from(detailKeysSet),
  };
}

export interface CsvExportOptions {
  avoidAbsent?: boolean;
}

export function flattenReportDataToCsv(
  records: BaseDataRecord[],
  selectedColumns?: string[],
  options?: CsvExportOptions
): string {
  if (!records || records.length === 0) {
    return '';
  }

  const avoidAbsent = !!options?.avoidAbsent;
  const { mainKeys, detailKeys } = getAvailableCsvColumns(records);
  const mainKeysSet = new Set(mainKeys);
  const detailKeysSet = new Set(detailKeys);

  // Preserve the caller-specified sequence when selectedColumns is provided
  let allHeaders: string[];
  if (selectedColumns && selectedColumns.length > 0) {
    allHeaders = selectedColumns.filter((col) => mainKeysSet.has(col) || detailKeysSet.has(col));
  } else {
    allHeaders = [...mainKeys, ...detailKeys];
  }

  if (allHeaders.length === 0) {
    return '';
  }

  const rows: string[] = [allHeaders.map(escapeCsvCell).join(',')];

  // Helper to extract value based on column name
  const extractVal = (col: string, emp: BaseDataRecord, day?: any): any => {
    if (day && col in day) {
      return day[col];
    }
    if (col in emp) {
      return emp[col];
    }
    if (day && detailKeysSet.has(col)) {
      return day[col];
    }
    if (mainKeysSet.has(col)) {
      return emp[col];
    }
    return '';
  };

  // Data lines (1-to-N)
  for (const emp of records) {
    let dailyRecords = emp.attandanceInfo || emp.attendanceInfo;

    if (avoidAbsent && dailyRecords) {
      dailyRecords = dailyRecords.filter((day) => !day.isAbsence);
    }

    if (!dailyRecords || dailyRecords.length === 0) {
      // If avoidAbsent is enabled, skip employees with no non-absent records
      if (avoidAbsent) {
        continue;
      }

      // Empty attendance row if employee was kept
      const rowValues = allHeaders.map((col) => {
        if (detailKeysSet.has(col)) {
          return '';
        }
        return extractVal(col, emp);
      });
      rows.push(rowValues.map(escapeCsvCell).join(','));
      continue;
    }

    for (const day of dailyRecords) {
      const rowValues = allHeaders.map((col) => extractVal(col, emp, day));
      rows.push(rowValues.map(escapeCsvCell).join(','));
    }
  }

  return rows.join('\r\n');
}

export function downloadCsvFile(csvContent: string, filename: string) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy to clipboard', err);
    return false;
  }
}
